import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Col, Button, Spin, Radio, Select, Modal } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'

import { TitleAndDivider } from '@components'
import { useConfigurationProperties, useEquipment } from '@hooks'
import { Equipment, RoleEnum } from '@types'
import { useUser } from '@contexts'

import {
  Container,
  StyledRow,
  StyledText,
  StyledInputNumber,
} from '../SettingsDashboard/components'

const stableSerializeTonsMap = (m: Record<string, number>) => {
  const sortedKeys = Object.keys(m).sort((a, b) => {
    if (a === 'default') return -1
    if (b === 'default') return 1
    return a.localeCompare(b, undefined, { numeric: true })
  })
  const ordered: Record<string, number> = {}
  sortedKeys.forEach(k => {
    ordered[k] = m[k]
  })
  return JSON.stringify(ordered)
}

const VehiclesSettings = () => {
  const { state } = useUser()
  const roles = state?.roles.map(role => role.name)
  const { ADMINRO } = RoleEnum
  const { t } = useTranslation()
  const {
    isLoading,
    poBlockEnabled,
    poBlockEnabledId,
    poBlockMinTons,
    poBlockMinTonsId,
    poBlockTonsPerEquipmentMap,
    poBlockTonsPerEquipmentId,
    updatePoBlockEnabled,
    updatePoBlockMinTons,
    updatePoBlockTonsPerEquipment,
    isLoadingPoBlockEnabled,
    isLoadingPoBlockMinTons,
    isLoadingPoBlockTonsPerEquipment,
  } = useConfigurationProperties()

  const { equipments, isLoading: isLoadingEquipments } = useEquipment()

  const [poBlockRadioDraft, setPoBlockRadioDraft] = useState<number>()
  const [newMinTons, setNewMinTons] = useState<number | null>()
  const [defaultDraft, setDefaultDraft] = useState<number>(20)
  const [draftByEquipmentKey, setDraftByEquipmentKey] = useState<Record<string, number>>({})
  const [addEquipmentId, setAddEquipmentId] = useState<number | undefined>(undefined)
  const [addTons, setAddTons] = useState<number | null>(20)

  const tonsMapSnapshot = useMemo(
    () => stableSerializeTonsMap(poBlockTonsPerEquipmentMap),
    [poBlockTonsPerEquipmentMap],
  )

  useEffect(() => {
    setPoBlockRadioDraft(poBlockEnabled === 'true' ? 1 : 0)
  }, [poBlockEnabled])

  useEffect(() => {
    if (poBlockMinTons !== undefined) setNewMinTons(poBlockMinTons)
  }, [poBlockMinTons])

  useEffect(() => {
    setDefaultDraft(poBlockTonsPerEquipmentMap.default ?? 20)
    const next: Record<string, number> = {}
    Object.keys(poBlockTonsPerEquipmentMap).forEach(k => {
      if (k !== 'default') next[k] = poBlockTonsPerEquipmentMap[k]
    })
    setDraftByEquipmentKey(next)
    setAddTons(poBlockTonsPerEquipmentMap.default ?? 20)
  }, [tonsMapSnapshot])

  const equipmentById = useMemo(() => {
    const m = new Map<string, Equipment>()
    ;(equipments ?? []).forEach(e => {
      if (e.id != null) m.set(String(e.id), e)
    })
    return m
  }, [equipments])

  const sortedOverrideKeys = useMemo(
    () =>
      Object.keys(draftByEquipmentKey).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true }),
      ),
    [draftByEquipmentKey],
  )

  const equipmentsAvailableToAdd = useMemo(() => {
    const inMap = new Set(Object.keys(poBlockTonsPerEquipmentMap))
    return [...(equipments ?? [])]
      .filter(e => e.id != null && !inMap.has(String(e.id)))
      .sort((a, b) => a.nameShort.localeCompare(b.nameShort, undefined, { sensitivity: 'base' }))
  }, [equipments, poBlockTonsPerEquipmentMap])

  const persistTonsMap = (next: Record<string, number>) => {
    if (poBlockTonsPerEquipmentId == null) return
    const withDefault: Record<string, number> = {
      ...next,
      default: next.default ?? poBlockTonsPerEquipmentMap.default ?? 20,
    }
    updatePoBlockTonsPerEquipment({
      id: poBlockTonsPerEquipmentId,
      value: JSON.stringify(withDefault),
    })
  }

  const handlePoBlockEnabledSave = (value: number) => {
    if (!poBlockEnabledId) return
    updatePoBlockEnabled({
      id: poBlockEnabledId,
      value: value === 1 ? 'true' : 'false',
    })
  }

  const handleMinTonsSave = () => {
    if (poBlockMinTonsId == null || newMinTons == null || Number.isNaN(newMinTons)) return
    updatePoBlockMinTons({ id: poBlockMinTonsId, value: String(newMinTons) })
  }

  const handleDefaultTonsSave = () => {
    if (defaultDraft == null || Number.isNaN(defaultDraft)) return
    const next = { ...poBlockTonsPerEquipmentMap, default: defaultDraft }
    persistTonsMap(next)
  }

  const handleEquipmentRowSave = (key: string) => {
    const v = draftByEquipmentKey[key]
    if (v == null || Number.isNaN(v)) return
    const next = { ...poBlockTonsPerEquipmentMap, [key]: v }
    persistTonsMap(next)
  }

  const handleEquipmentDelete = (key: string) => {
    const next = { ...poBlockTonsPerEquipmentMap }
    delete next[key]
    if (next.default === undefined) next.default = poBlockTonsPerEquipmentMap.default ?? 20
    persistTonsMap(next)
  }

  const confirmDeleteEquipment = (key: string) => {
    Modal.confirm({
      title: t('admin_router.admin_dashboard.settings.vehicles.po_block_tons_remove_confirm_title'),
      okText: t('admin_router.admin_dashboard.settings.vehicles.po_block_tons_remove_confirm_ok'),
      cancelText: t(
        'admin_router.admin_dashboard.settings.vehicles.po_block_tons_remove_confirm_cancel',
      ),
      onOk: () => handleEquipmentDelete(key),
      icon: null,
    })
  }

  const handleAddEquipment = () => {
    if (addEquipmentId == null || addTons == null || Number.isNaN(addTons)) return
    const k = String(addEquipmentId)
    if (poBlockTonsPerEquipmentMap[k] !== undefined) return
    const next = { ...poBlockTonsPerEquipmentMap, [k]: addTons }
    persistTonsMap(next)
    setAddEquipmentId(undefined)
    setAddTons(poBlockTonsPerEquipmentMap.default ?? 20)
  }

  const equipmentLabel = (key: string) => {
    const eq = equipmentById.get(key)
    if (!eq) {
      return t('admin_router.admin_dashboard.settings.vehicles.po_block_tons_unknown_equipment', {
        id: key,
      })
    }
    const primary = eq.nameShort || eq.name
    const suffix = eq.nameShort && eq.name !== eq.nameShort ? ` (${eq.name})` : ''
    return `${primary}${suffix}`
  }

  const currentRadio = poBlockEnabled === 'true' ? 1 : 0
  const isAdminRO = roles?.includes(ADMINRO)
  const tonsSaveDisabled =
    isAdminRO || isLoadingPoBlockTonsPerEquipment || poBlockTonsPerEquipmentId == null

  return (
    <Container>
      <TitleAndDivider title={t('admin_router.admin_dashboard.settings.vehicles.title')} />

      <Spin
        spinning={
          isLoading || isLoadingEquipments || isLoadingPoBlockEnabled || isLoadingPoBlockMinTons
        }
      >
        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.vehicles.po_block_enabled')}:
            </StyledText>
          </Col>
          {isAdminRO ? (
            <Radio.Group size="large" value={poBlockRadioDraft}>
              <Col span={24}>
                <Radio value={1} disabled>
                  {t('admin_router.admin_dashboard.settings.yes')}
                </Radio>
                <Radio value={0} disabled>
                  {t('admin_router.admin_dashboard.settings.no')}
                </Radio>
              </Col>
            </Radio.Group>
          ) : (
            <Radio.Group size="large" value={poBlockRadioDraft}>
              <Col span={24}>
                <Radio value={1} onChange={e => setPoBlockRadioDraft(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.yes')}
                </Radio>
                <Radio value={0} onChange={e => setPoBlockRadioDraft(e.target.value)}>
                  {t('admin_router.admin_dashboard.settings.no')}
                </Radio>
              </Col>
            </Radio.Group>
          )}
          <Col span={3}>
            <Button
              onClick={() =>
                poBlockRadioDraft !== undefined && handlePoBlockEnabledSave(poBlockRadioDraft)
              }
              disabled={
                isAdminRO ||
                poBlockRadioDraft === undefined ||
                poBlockRadioDraft === currentRadio ||
                poBlockEnabledId == null
              }
            >
              {t('ta_invoice.new_invoice.update')}
            </Button>
          </Col>
        </StyledRow>

        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.vehicles.po_block_min_tons')}:
            </StyledText>
          </Col>
          <Col span={2}>
            <StyledInputNumber
              min={0}
              value={newMinTons ?? undefined}
              onChange={v => setNewMinTons(v as number | null)}
              disabled={isAdminRO}
            />
          </Col>
          <Col span={3}>
            <Button
              onClick={handleMinTonsSave}
              disabled={
                isAdminRO ||
                poBlockMinTonsId == null ||
                newMinTons === null ||
                newMinTons === undefined ||
                newMinTons === poBlockMinTons
              }
            >
              {t('ta_invoice.new_invoice.update')}
            </Button>
          </Col>
        </StyledRow>

        <StyledRow>
          <Col span={24}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.vehicles.po_block_tons_per_equipment')}:
            </StyledText>
            <StyledText style={{ display: 'block', fontSize: 12, opacity: 0.85, marginTop: 4 }}>
              {t('admin_router.admin_dashboard.settings.vehicles.po_block_tons_per_equipment_hint')}
            </StyledText>
          </Col>
        </StyledRow>

        <StyledRow>
          <Col span={8}>
            <StyledText>
              {t('admin_router.admin_dashboard.settings.vehicles.po_block_tons_default')}:
            </StyledText>
          </Col>
          <Col span={2}>
            <StyledInputNumber
              min={0}
              value={defaultDraft}
              onChange={v => setDefaultDraft((v as number | null) ?? defaultDraft)}
              disabled={isAdminRO}
            />
          </Col>
          <Col span={3}>
            <Button
              onClick={handleDefaultTonsSave}
              disabled={
                tonsSaveDisabled ||
                defaultDraft === (poBlockTonsPerEquipmentMap.default ?? 20) ||
                defaultDraft == null ||
                Number.isNaN(defaultDraft)
              }
            >
              {t('ta_invoice.new_invoice.update')}
            </Button>
          </Col>
        </StyledRow>

        {sortedOverrideKeys.map(key => (
          <StyledRow key={key} align="middle">
            <Col span={8}>
              <StyledText>{equipmentLabel(key)}:</StyledText>
            </Col>
            <Col span={2}>
              <StyledInputNumber
                min={0}
                value={draftByEquipmentKey[key]}
                onChange={v =>
                  setDraftByEquipmentKey(prev => ({
                    ...prev,
                    [key]: (v as number | null) ?? prev[key],
                  }))
                }
                disabled={isAdminRO}
              />
            </Col>
            <Col span={3}>
              <Button
                onClick={() => handleEquipmentRowSave(key)}
                disabled={
                  tonsSaveDisabled ||
                  draftByEquipmentKey[key] === poBlockTonsPerEquipmentMap[key] ||
                  draftByEquipmentKey[key] == null ||
                  Number.isNaN(draftByEquipmentKey[key])
                }
              >
                {t('ta_invoice.new_invoice.update')}
              </Button>
            </Col>
            <Col span={2}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label={t(
                  'admin_router.admin_dashboard.settings.vehicles.po_block_tons_remove',
                )}
                onClick={() => confirmDeleteEquipment(key)}
                disabled={tonsSaveDisabled}
              />
            </Col>
          </StyledRow>
        ))}

        {equipmentsAvailableToAdd.length > 0 && (
          <>
            <StyledRow>
              <Col span={24}>
                <StyledText>
                  {t('admin_router.admin_dashboard.settings.vehicles.po_block_tons_add_section')}:
                </StyledText>
              </Col>
            </StyledRow>
            <StyledRow align="middle">
              <Col span={8}>
                <Select<number>
                  style={{ width: '100%' }}
                  placeholder={t(
                    'admin_router.admin_dashboard.settings.vehicles.po_block_tons_select_placeholder',
                  )}
                  value={addEquipmentId}
                  onChange={v => setAddEquipmentId(v)}
                  allowClear
                  disabled={isAdminRO || equipmentsAvailableToAdd.length === 0}
                  options={equipmentsAvailableToAdd.map(e => ({
                    value: e.id!,
                    label: `${e.nameShort || e.name}${
                      e.nameShort && e.name !== e.nameShort ? ` (${e.name})` : ''
                    }`,
                  }))}
                />
              </Col>
              <Col span={2}>
                <StyledInputNumber
                  min={0}
                  value={addTons ?? undefined}
                  onChange={v => setAddTons(v as number | null)}
                  disabled={isAdminRO}
                />
              </Col>
              <Col span={4}>
                <Button
                  type="primary"
                  onClick={handleAddEquipment}
                  disabled={
                    tonsSaveDisabled ||
                    addEquipmentId == null ||
                    addTons == null ||
                    Number.isNaN(addTons)
                  }
                >
                  {t('admin_router.admin_dashboard.settings.vehicles.po_block_tons_add_button')}
                </Button>
              </Col>
            </StyledRow>
          </>
        )}
      </Spin>
    </Container>
  )
}

export { VehiclesSettings }
